import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { supabase } from '../../lib/supabase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '../../context/UserContext';
import { Card, Button, Input, Badge, SidePanel, Switch, Icon } from '../../components/ui';

interface Agency {
  id: string;
  name: string;
  website: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const emptyForm = {
  name: '',
  website: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'United States',
  notes: '',
  is_active: true,
};

const domainOf = (website: string | null) => {
  if (!website) return null;
  try {
    return new URL(website.startsWith('http') ? website : `https://${website}`).hostname.replace(
      /^www\./,
      ''
    );
  } catch {
    return website;
  }
};

function StatTile({ label, value, mono = true }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 12px',
        minWidth: 0,
      }}
    >
      <div className="t-eyebrow" style={{ marginBottom: '4px' }}>
        {label}
      </div>
      <div
        className={mono ? 't-num' : ''}
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
          color: value ? 'var(--text-body)' : 'var(--text-faint)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={value || undefined}
      >
        {value || '—'}
      </div>
    </div>
  );
}

export default function AgenciesPanel() {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; agency: Agency | null }>({
    open: false,
    agency: null,
  });

  const [formData, setFormData] = useState({ ...emptyForm });

  const { user } = useUser();

  const canManageAgencies = user?.role === 'admin' || user?.role === 'agency_owner';

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAgencies((data || []) as Agency[]);
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (agency?: Agency) => {
    if (agency) {
      setEditingAgency(agency);
      setFormData({
        name: agency.name,
        website: agency.website || '',
        email: agency.email || '',
        phone: agency.phone || '',
        address: agency.address || '',
        city: agency.city || '',
        state: agency.state || '',
        zip: agency.zip || '',
        country: agency.country || 'United States',
        notes: agency.notes || '',
        is_active: agency.is_active,
      });
    } else {
      setEditingAgency(null);
      setFormData({ ...emptyForm });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAgency(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Agency name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const agencyData = {
        ...formData,
        updated_by: user?.id,
        updated_at: new Date().toISOString(),
      };

      if (editingAgency) {
        const { error } = await supabase.from('agencies').update(agencyData).eq('id', editingAgency.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('agencies').insert([{ ...agencyData, created_by: user?.id }]);

        if (error) throw error;
      }

      await fetchAgencies();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving agency:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.agency) return;

    try {
      const { error } = await supabase.from('agencies').delete().eq('id', deleteConfirm.agency.id);

      if (error) throw error;

      await fetchAgencies();
      setDeleteConfirm({ open: false, agency: null });
    } catch (err) {
      console.error('Error deleting agency:', err);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const filteredAgencies = agencies.filter(
    (agency) =>
      agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <Icon name="circle-alert" size={15} />
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            className="pressable"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex items-center justify-between" style={{ gap: '10px', marginBottom: 'var(--space-4)' }}>
        <div style={{ width: '220px' }}>
          <Input
            icon="search"
            fieldSize="sm"
            placeholder="Search agencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {canManageAgencies && (
          <Button size="sm" icon="plus" onClick={() => handleOpenModal()}>
            Add Agency
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card>
          <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
            <div
              className="animate-spin"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-pill)',
                border: '3px solid var(--border-hairline)',
                borderTopColor: 'var(--accent)',
                marginBottom: 'var(--space-3)',
              }}
            />
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading...</p>
          </div>
        </Card>
      ) : filteredAgencies.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
            <FontAwesomeIcon
              icon={faBuilding}
              style={{ fontSize: '22px', color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}
            />
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              {searchTerm ? 'No agencies found' : 'No agencies yet'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {filteredAgencies.map((agency) => (
            <Card
              key={agency.id}
              eyebrow="Agency"
              title={agency.name}
              actions={
                agency.is_active ? (
                  <Badge tone="up" dot>
                    Active
                  </Badge>
                ) : (
                  <Badge tone="idle">Inactive</Badge>
                )
              }
            >
              <div
                className="grid grid-cols-1 sm:grid-cols-3"
                style={{ gap: '8px', marginBottom: 'var(--space-4)' }}
              >
                <StatTile label="Domain" value={domainOf(agency.website)} />
                <StatTile label="Email" value={agency.email} />
                <StatTile
                  label="Location"
                  value={[agency.city, agency.state].filter(Boolean).join(', ') || null}
                  mono={false}
                />
              </div>
              {agency.phone && (
                <div
                  className="t-num"
                  style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginBottom: 'var(--space-4)' }}
                >
                  {agency.phone}
                </div>
              )}
              {canManageAgencies && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button size="sm" variant="secondary" icon="pencil" onClick={() => handleOpenModal(agency)}>
                    Edit Agency
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="trash-2"
                    style={{ color: 'var(--danger-400)' }}
                    onClick={() => setDeleteConfirm({ open: true, agency })}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit panel */}
      <SidePanel
        open={isModalOpen}
        onClose={handleCloseModal}
        eyebrow="Agency"
        title={editingAgency ? 'Edit Agency' : 'Add Agency'}
        width={440}
        footer={
          <>
            <Button
              icon={editingAgency ? 'check' : 'plus'}
              disabled={isSubmitting || !formData.name.trim()}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Saving...' : editingAgency ? 'Save Changes' : 'Add Agency'}
            </Button>
            <Button variant="ghost" disabled={isSubmitting} onClick={handleCloseModal}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Agency Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter agency name..."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '14px' }}>
            <Input
              label="Website"
              type="url"
              icon="globe"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://example.com"
            />
            <Input
              label="Email"
              type="email"
              icon="mail"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="contact@agency.com"
            />
          </div>
          <Input
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="(555) 123-4567"
          />
          <Input
            label="Address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="123 Main St"
          />
          <div className="grid grid-cols-3" style={{ gap: '14px' }}>
            <Input
              label="City"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="City"
            />
            <Input
              label="State"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              placeholder="State"
            />
            <Input
              label="ZIP"
              value={formData.zip}
              onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
              placeholder="12345"
            />
          </div>
          <Input
            label="Country"
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            placeholder="United States"
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Notes</span>
            <textarea
              className="ck-textarea"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
            />
          </label>
          <Switch
            checked={formData.is_active}
            onChange={(checked) => setFormData({ ...formData, is_active: checked })}
            label={<span style={{ fontSize: 'var(--text-sm)' }}>Active</span>}
          />
        </div>
      </SidePanel>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, agency: null })}
        className="relative z-50"
      >
        <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
          <Dialog.Panel
            className="mx-auto w-full ck-menu-pop"
            style={{
              maxWidth: '400px',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-pop)',
              padding: 'var(--space-6)',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
              Agency
            </div>
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--text-body)',
              }}
            >
              Delete Agency
            </Dialog.Title>

            <p style={{ margin: 'var(--space-3) 0 var(--space-5)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              Are you sure you want to delete <strong>{deleteConfirm.agency?.name}</strong>? This action
              cannot be undone.
            </p>

            <div className="flex justify-end" style={{ gap: '10px' }}>
              <Button variant="ghost" onClick={() => setDeleteConfirm({ open: false, agency: null })}>
                Cancel
              </Button>
              <Button variant="danger" icon="trash-2" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
